/**
 * Lightweight, imperative toast notification component.
 * Renders temporary notifications at bottom-center of viewport.
 *
 * Usage:
 *   import { showToast } from '@/components/shared/Toast';
 *   showToast('PDF downloaded!');
 *   showToast('Processing...', { duration: 5000 });
 */
import { createRoot } from 'react-dom/client';

let activeContainer = null;
let activeRoot = null;
let dismissTimer = null;

function ToastContent({ message, visible }) {
  return (
    <div
      className={`fixed bottom-6 left-1/2 z-[9999] -translate-x-1/2 rounded-xl bg-slate-800 px-4 py-2.5 text-sm font-medium text-white shadow-lg transition-all duration-300 ${
        visible ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0'
      }`}
    >
      {message}
    </div>
  );
}

function cleanup() {
  if (activeRoot) {
    activeRoot.unmount();
    activeRoot = null;
  }
  if (activeContainer) {
    document.body.removeChild(activeContainer);
    activeContainer = null;
  }
}

/**
 * Show a toast notification at the bottom-center of the viewport.
 *
 * @param {string} message - Text to display
 * @param {Object} [options]
 * @param {number} [options.duration=3000] - Auto-dismiss after this many milliseconds
 */
export function showToast(message, { duration = 3000 } = {}) {
  // Clear any existing toast
  if (dismissTimer) clearTimeout(dismissTimer);
  cleanup();

  // Create portal container
  activeContainer = document.createElement('div');
  activeContainer.setAttribute('data-toast-portal', '');
  document.body.appendChild(activeContainer);
  activeRoot = createRoot(activeContainer);

  // Render hidden first, then visible (triggers CSS transition)
  activeRoot.render(<ToastContent message={message} visible={false} />);

  // Trigger entrance animation on next frame
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      if (activeRoot) {
        activeRoot.render(<ToastContent message={message} visible={true} />);
      }
    });
  });

  // Auto-dismiss
  dismissTimer = setTimeout(() => {
    if (activeRoot) {
      activeRoot.render(<ToastContent message={message} visible={false} />);
      // Clean up after fade-out animation
      setTimeout(cleanup, 350);
    }
  }, duration);
}
